import { Inject, Injectable } from '@angular/core';

import { NumberingItem, NumberingQuery, propertyChangedJoinPointFilter, propertyChangingJoinPointFilter } from '@skylab-shared/business';
import { BusinessObjectBase, ModelBase, PropertyChangedJoinPoint, PropertyChangingJoinPoint, RootContextBase, Weaver } from '@skylab-shared/core';
import { InventoryDocumentKind, PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { dateDiffInDays } from '@skylab-shared/utils';

import { RootContext } from '@skylab-angular-shared/core';
import { DialogInsService } from '@skylab-angular-shared/ui';

import { Constructor, Dictionary } from '@skylab-shared/types';
import { NumberingChangeUnavailableDialogComponent } from '../dialogs/numbering-change-unavailable/numbering-change-unavailable-dialog.component';
import { parseNumberingFormat } from './form-utils';

type ModelPart = {
    numberingId: number;
    documentNumber: string;
} & ModelBase;

export const superviseNumberingChanging = <T extends ModelPart>(
    weaver: Weaver,
    bo: BusinessObjectBase<T>,
    dialogService: DialogInsService,
    refresher: FormNumberingRefresher
) => {
    const cache = <Dictionary<string>> {};
    const numberings = bo.localContext.getPreloaded(NumberingQuery);
    const deregisterFunctions = <Function[]>[];
    let previousNumbering: NumberingItem | undefined;

    deregisterFunctions.push(weaver.register(PropertyChangingJoinPoint, propertyChangingJoinPointFilter(bo)(<Constructor<T>> bo.model.constructor, ['numberingId'])(
        ctx => {
            if (bo.model.isPersistent && numberings) {
                previousNumbering ??= numberings.find(elem => elem.id === bo.model.numberingId);
                const currentNumbering = numberings.find(elem => elem.id === ctx.newValue);

                if (previousNumbering && currentNumbering) {
                    const numberingTypeChanged = previousNumbering.manual !== currentNumbering.manual;

                    if (numberingTypeChanged) {
                        ctx.cancel();
                        refresher.refresh();
                        dialogService.showAsync({component: NumberingChangeUnavailableDialogComponent, componentProps: { data: { isPreviousNumberingManual: previousNumbering.manual } }});
                    }
                }
            }
        }
    )));

    deregisterFunctions.push(weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(bo)(<Constructor<T>> bo.model.constructor, ['numberingId'])(
        ctx => {
            const document = <T> ctx.model;
            cache[ctx.oldValue] = document.documentNumber;
            document.documentNumber = cache[document.numberingId] || '';
        }
    )));

    return () => deregisterFunctions.forEach(f => f());
};

export type NumberingItemWithPreview = NumberingItem & { formatPreview: string | null; };
export type DocumentNumber = {
    readonly id: number;
    readonly issueDate: Date;
    readonly number: string;
};

export const refreshNumberingsFormatPreview = (items: NumberingItemWithPreview[], issueDate: Date, documentNumber?: DocumentNumber): NumberingItemWithPreview[] => (
    items.map(item => (
        item.formatPreview = documentNumber && item.id === documentNumber.id && dateDiffInDays(documentNumber.issueDate, issueDate) === 0
            ? documentNumber.number
            : item.manual
                ? null
                : parseNumberingFormat(issueDate, item.format),
        item
    ))
);

export type Document = {
    issueDate: Date | null;
    numberingId: number;
    documentNumber: string;
    kind: SaleDocumentKind | PurchaseDocumentKind | InventoryDocumentKind;
} & ModelBase;

export interface FormNumberingRefresher {
    getAvailableNumberings(): NumberingItemWithPreview[];
    refresh(): void;
}

@Injectable({
    providedIn: 'root'
})
export class FormNumberingService {

    public constructor(
        @Inject(RootContext) private readonly rootContext: RootContextBase
    ) { }

    public async createAsync(bo: BusinessObjectBase<Document>, onNumberingRefresh: (numberings: NumberingItemWithPreview[]) => void): Promise<Refresher> {
        const numberings = await this.rootContext.getAsync(NumberingQuery);
        return new Refresher(numberings, bo, onNumberingRefresh);
    }
}

class Refresher implements FormNumberingRefresher {
    private readonly documentNumber: DocumentNumber | null;

    public constructor(
        private readonly numberings: NumberingItem[],
        private readonly bo: BusinessObjectBase<Document>,
        private readonly onNumberingRefresh: (numberings: NumberingItemWithPreview[]) => void
    ) {
        this.documentNumber = bo.model.isPersistent && bo.model.issueDate ? {
            id: bo.model.numberingId,
            number: bo.model.documentNumber,
            issueDate: bo.model.issueDate
        } : null;
    }

    public refresh(): void {
        const numberings = this.getAvailableNumberings();
        this.onNumberingRefresh(numberings);
    }

    public getAvailableNumberings(): any {
        const availableNumberings = refreshNumberingsFormatPreview(
            this.numberings
                .filter(n => n.kinds.indexOf(this.bo.model.kind) > -1)
                .filter(item => item.active || (item.id === this.documentNumber?.id))
                .map(numbering => ({ ...numbering, formatPreview: null })),
            this.bo.model.issueDate!,
            this.documentNumber || undefined
        );
        return availableNumberings;
    }
}
